/**
 * Upgrades a legacy schemaVersion 1|2 content blob to schemaVersion 3 by
 * wrapping the job-wide arrays into a single default form.
 *
 * If the input is already schemaVersion 3, it is returned by identity
 * (same reference, no copy).
 *
 * The parameter is intentionally broad (`| Record<string, unknown>`) so
 * callers that receive a raw Prisma JSONB value do not need an `as any` cast —
 * the function already defends against partial/sparse blobs internally.
 */
export function migrateContentToV3(c) {
    if (c.schemaVersion === 3) {
        return c;
    }
    // Defensive against partial legacy blobs (e.g. minimal `{ schemaVersion: 1 }`
    // stubs or hand-edited content): every legacy array is optional here so the
    // upgrade never throws on a missing field. A real published V1/V2 blob always
    // carries these, but restore/cherry-pick can receive sparse historical rows.
    const legacy = c;
    const exportDestinations = [];
    const exportMappings = [];
    for (const d of legacy.export?.destinations ?? []) {
        const { mappings, ...dest } = d;
        exportDestinations.push(dest);
        exportMappings.push({ destinationId: d.id, mappings: mappings ?? [] });
    }
    const defaultForm = {
        id: '__default__',
        name: 'Default',
        classificationKey: '__default__',
        isDefault: true,
        displayOrder: 0,
        fields: (legacy.fields ?? []).filter((f) => !f.registryScope || f.registryScope === 'jobtype'),
        recognition: legacy.formRecognition,
        rules: legacy.rules ?? [],
        exportMappings,
    };
    return {
        schemaVersion: 3,
        pipeline: legacy.pipeline ?? { stages: [] },
        headerMapping: legacy.headerMapping ?? { format: 'none', paths: {} },
        identity: legacy.identity,
        profiles: legacy.profiles,
        exportDestinations,
        forms: [defaultForm],
        // P8 review fix — a legacy V2 blob carrying constants must not lose them on
        // the V2→V3 upgrade (flatten-legacy carries them V3→V2; this is the other
        // direction — the loader runs migrate THEN flatten, so dropping here made
        // constants dead for every legacy-content job at every site).
        ...(legacy.constants ? { constants: legacy.constants } : {}),
    };
}
//# sourceMappingURL=content-migrate.js.map